import { useEffect, useState } from "react";
import {
  claimCallSound,
  getCallSound,
  isCallAudioPrimed,
  releaseCallSound,
  type CallSoundName,
} from "../lib/callAudio";

/**
 * Play one of the call module's looping sounds for as long as `enabled` holds.
 *
 * The whole design of ringing is "bind the sound to a UI lifetime, not to an
 * event": the caller's ringback lives exactly as long as the ringing route is
 * showing `status === "ringing"`, and the callee's ringtone lives exactly as
 * long as the incoming banner has an invite to show. Every way a call can end
 * — answered, declined, cancelled, timed out, or the user navigating away —
 * already tears down that UI, so all of them stop the sound for free. There is
 * no per-outcome stop() to forget, and no way for a missed status transition to
 * strand a tone playing forever.
 *
 * Returns `blocked`: true when the browser refused `play()`. That happens when
 * the user has not interacted with the page yet, so `primeCallAudio()` never
 * banked the autoplay permission — a real case, since a tab restored on startup
 * and never clicked can still receive a call. Ringing silently in that state is
 * the one failure the feature cannot tolerate, so the flag is surfaced rather
 * than swallowed; D5 drives the tab-title flash from it.
 *
 * Single-owner assumption: one mounted component per sound. That holds today —
 * `IncomingCallBanner` is mounted once app-wide and `OutgoingRingingRoute` is a
 * route — and it matters because the <audio> elements are shared singletons, so
 * a second owner's cleanup would pause the first owner's playback. Arbitration
 * *between* the two different sounds is D7; arbitration across tabs is D8.
 */
export function useCallSound(name: CallSoundName, enabled: boolean): { blocked: boolean } {
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const el = getCallSound(name);
    let disposed = false;

    // D3 — take ownership before touching the element, so an in-flight
    // priming cleanup cannot pause this ring the moment it starts.
    claimCallSound(name);

    // Start from the top: the element is reused for the app's lifetime, so a
    // previous ring left `currentTime` wherever it was stopped.
    el.currentTime = 0;
    el.muted = false;

    const attempt = el.play();
    if (attempt && typeof attempt.then === "function") {
      attempt.then(
        () => {
          if (!disposed) setBlocked(false);
        },
        (err: unknown) => {
          // Expected when no user gesture has happened yet this page-load.
          if (disposed) return;
          setBlocked(true);
          console.warn(
            `useCallSound(${name}): playback blocked` +
              (isCallAudioPrimed() ? "" : " (audio was never primed — no user gesture yet)"),
            err,
          );
        },
      );
    }

    return () => {
      disposed = true;
      releaseCallSound(name);
      el.pause();
      el.currentTime = 0;
    };
  }, [name, enabled]);

  // Clear a stale "blocked" once the sound is no longer wanted, so the next
  // ring starts from a clean slate rather than flashing the title immediately.
  useEffect(() => {
    if (!enabled && blocked) setBlocked(false);
  }, [enabled, blocked]);

  return { blocked };
}
